import React, { useEffect, useState, useCallback } from "react";
import { Bell, BellOff, BellRing, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  urlBase64ToUint8Array,
  arrayBufferToBase64Url,
  detectPushSupport as detectSupport,
  syncCoachPush,
  getCoachPushStatus,
} from "../../lib/coachPush";

/**
 * CoachPushSettings — Enables/disables Web Push notifications for the coach
 * on this specific device.
 *
 * Notes on Web Push edge cases handled below:
 *   - iOS 16.4+ Safari only supports push when the site is installed as a
 *     PWA (Add to Home Screen). We detect and show a hint chip.
 *   - Android Chrome: if the user (or system) previously denied notifications
 *     at the site or system level, `Notification.requestPermission()` no
 *     longer shows a prompt and returns "denied" instantly.
 *   - If an old subscription exists with a stale VAPID key (e.g. the coach
 *     tried enabling on preview vs prod), a plain `subscribe(...)` call will
 *     throw InvalidStateError. We defensively `unsubscribe()` the old one
 *     first when the applicationServerKey doesn't match.
 *   - Battery savers + some launchers on Android can also throttle push. We
 *     surface the "Diagnose" panel so the coach can copy the current state
 *     and share it with support.
 */

export default function CoachPushSettings() {
  const [status, setStatus] = useState("checking"); // checking | unsupported | ios-not-pwa | idle | subscribing | subscribed | denied
  const [busyTest, setBusyTest] = useState(false);
  const [showDiag, setShowDiag] = useState(false);
  const [diag, setDiag] = useState(null);
  const [lastError, setLastError] = useState("");
  const [serverStatus, setServerStatus] = useState(null);

  const runDiag = useCallback(async () => {
    const det = detectSupport();
    const info = {
      supported: det.supported,
      isPwa:
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true,
      permission: typeof Notification !== "undefined" ? Notification.permission : "n/a",
      userAgent: navigator.userAgent || "",
      href: window.location.href,
      swRegistration: "not-registered",
      pushSubscription: "none",
      backendPublicKey: "unknown",
      lastError: lastError || "",
    };
    try {
      const { data } = await api.get("/coaching/push/vapid-public-key");
      info.backendPublicKey = (data?.public_key || "").slice(0, 20) + "…";
    } catch {
      info.backendPublicKey = "failed to fetch";
    }
    try {
      const st = await getCoachPushStatus();
      info.serverDeviceCount = st.device_count;
      info.lastPushAttempt = st.last_attempt;
      info.recentPushAttempts = (st.recent || []).map((r) => ({
        at: r.at, title: r.title, devices: r.devices, sent: r.sent, purged: r.purged, errors: r.errors,
      }));
    } catch {
      info.serverDeviceCount = "failed to fetch";
    }
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw-coach-push.js");
      if (reg) {
        info.swRegistration = reg.active
          ? "active"
          : reg.installing
          ? "installing"
          : reg.waiting
          ? "waiting"
          : "registered";
        const sub = await reg.pushManager.getSubscription();
        info.pushSubscription = sub ? "present" : "none";
      }
    } catch (e) {
      info.swRegistration = `error: ${e.message}`;
    }
    setDiag(info);
  }, [lastError]);

  const loadServerStatus = useCallback(() => {
    getCoachPushStatus().then(setServerStatus).catch(() => setServerStatus(null));
  }, []);

  // Self-healing: if permission is granted this re-creates a missing/stale
  // subscription and re-registers it with the backend, so "Notifications on"
  // is only shown when the server really has this device.
  const check = useCallback(async () => {
    try {
      const res = await syncCoachPush();
      setStatus(res === "unconfigured" ? "unsupported" : res);
    } catch {
      setStatus("idle");
    }
    loadServerStatus();
  }, [loadServerStatus]);

  useEffect(() => {
    check();
  }, [check]);

  const enable = async () => {
    setStatus("subscribing");
    setLastError("");
    const step = (n, m) => console.log(`[push] step ${n}:`, m);
    const fail = (msg, e) => {
      const detail = e?.name && e?.message ? `${e.name}: ${e.message}` : msg;
      console.error("[push] failed:", detail, e);
      setLastError(detail);
      toast.error(detail, { duration: 10000 });
      setStatus("idle");
    };

    try {
      step(1, "fetching VAPID public key");
      const { data } = await api.get("/coaching/push/vapid-public-key");
      const publicKey = data?.public_key || "";
      if (!publicKey) return fail("Push notifications aren't configured on the server.");

      step(2, `permission=${Notification.permission}`);
      if (Notification.permission === "denied") {
        setStatus("denied");
        return toast.error(
          "Notifications are blocked for this site. Open Chrome → Site Settings → Notifications and switch it back on, then reload.",
          { duration: 12000 }
        );
      }
      if (Notification.permission !== "granted") {
        const perm = await Notification.requestPermission();
        step(3, `permission after prompt = ${perm}`);
        if (perm === "denied") {
          setStatus("denied");
          return toast.error(
            "You tapped 'Block'. Open Chrome → Site Settings → Notifications for this site and allow, then try again.",
            { duration: 12000 }
          );
        }
        if (perm !== "granted") {
          setStatus("idle");
          return toast.error("Permission not granted.");
        }
      }

      step(4, "registering service worker");
      const reg = await navigator.serviceWorker.register("/sw-coach-push.js");
      // Wait for it to activate — with a hard timeout so we never hang forever.
      await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, rej) =>
          setTimeout(() => rej(new Error("Service worker didn't activate in 10s")), 10000)
        ),
      ]);
      step(5, "service worker ready");

      // If a subscription already exists, check whether it uses OUR VAPID key.
      // Chrome throws InvalidStateError when you subscribe with a different key
      // than the current subscription — we unsubscribe the stale one first.
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        const currentKey = arrayBufferToBase64Url(existing.options?.applicationServerKey);
        if (currentKey && currentKey !== publicKey) {
          step(6, "unsubscribing stale subscription (different VAPID key)");
          try {
            await existing.unsubscribe();
          } catch (e) {
            console.warn("stale unsubscribe failed (continuing)", e);
          }
        } else {
          step(6, "reusing existing subscription");
          await api.post("/coaching/push/subscribe", { subscription: existing.toJSON() });
          setStatus("subscribed");
          toast.success("Notifications on. Sending a test now…");
          try {
            setBusyTest(true);
            await api.post("/coaching/push/test");
          } finally {
            setBusyTest(false);
          }
          return;
        }
      }

      step(7, "calling pushManager.subscribe");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      step(8, "posting subscription to backend");
      await api.post("/coaching/push/subscribe", { subscription: sub.toJSON() });
      setStatus("subscribed");
      toast.success("Notifications on. Sending a test now…");
      try {
        setBusyTest(true);
        await api.post("/coaching/push/test");
      } finally {
        setBusyTest(false);
        loadServerStatus();
      }
    } catch (e) {
      fail("Couldn't enable push", e);
    }
  };

  const disable = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw-coach-push.js");
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        try {
          await api.post("/coaching/push/unsubscribe", { subscription: sub.toJSON() });
        } catch {
          /* server may already have purged it */
        }
        await sub.unsubscribe();
      }
      toast.success("Notifications turned off on this device.");
      setStatus("idle");
    } catch (e) {
      toast.error("Couldn't turn off — try refreshing this tab.");
    }
  };

  const sendTest = async () => {
    setBusyTest(true);
    try {
      const { data } = await api.post("/coaching/push/test");
      const sent = data?.sent ?? 0;
      if (sent > 0) {
        toast.success(`Test sent to ${sent} device${sent === 1 ? "" : "s"}.`);
      } else {
        toast.error("No devices are subscribed. Try 'Off' then 'Enable notifications' again on this device.");
      }
    } catch {
      toast.error("Couldn't send test.");
    } finally {
      setBusyTest(false);
      loadServerStatus();
    }
  };

  const DiagLink = (
    <button
      type="button"
      onClick={async () => {
        await runDiag();
        setShowDiag((v) => !v);
      }}
      data-testid="coach-push-diag-btn"
      className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-navy)] px-1"
      title="Show push diagnostic info"
    >
      <Info size={11} className="inline" />
    </button>
  );

  const DiagPanel =
    showDiag && diag ? (
      <pre
        className="mt-2 text-[10px] font-mono-tir text-[var(--tir-ink)] bg-[var(--tir-ivory)] border border-[var(--tir-border)] p-2 max-w-md whitespace-pre-wrap break-words"
        data-testid="coach-push-diag-panel"
      >
        {JSON.stringify(diag, null, 2)}
      </pre>
    ) : null;

  if (status === "checking") return null;
  if (status === "unsupported") return null;

  if (status === "ios-not-pwa") {
    return (
      <div
        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] border border-[var(--tir-border)] px-3 py-1.5"
        data-testid="coach-push-ios-hint"
        title="On iPhone, notifications only work when the portal is installed as an app. Tap Share → Add to Home Screen, then open the icon."
      >
        <Bell size={11} /> Push · install as app first
      </div>
    );
  }

  if (status === "denied") {
    return (
      <div className="flex flex-col items-end gap-1">
        <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-burgundy)] border border-[var(--tir-burgundy)]/40 px-3 py-1.5" data-testid="coach-push-denied">
          <BellOff size={11} /> Push blocked
          {DiagLink}
        </div>
        {DiagPanel}
      </div>
    );
  }

  if (status === "subscribed") {
    return (
      <div className="flex flex-col items-end gap-1" data-testid="coach-push-on">
        <div className="inline-flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold text-emerald-800 bg-[#eaf5ee] border border-emerald-800/30 px-3 py-1.5"
            title={serverStatus?.last_attempt ? `Last push "${serverStatus.last_attempt.title}" → ${serverStatus.last_attempt.sent}/${serverStatus.last_attempt.devices} devices at ${new Date(serverStatus.last_attempt.at).toLocaleString()}` : "No pushes sent yet"}
            data-testid="coach-push-on-badge"
          >
            <BellRing size={11} /> Notifications on
            {serverStatus?.device_count != null && (
              <span className="opacity-70" data-testid="coach-push-device-count">· {serverStatus.device_count} device{serverStatus.device_count === 1 ? "" : "s"}</span>
            )}
          </span>
          <button
            type="button"
            onClick={sendTest}
            disabled={busyTest}
            data-testid="coach-push-test-btn"
            className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-navy)] hover:text-[var(--tir-ink)] border border-[var(--tir-border)] hover:border-[var(--tir-navy)] px-2 py-1 transition-colors disabled:opacity-60"
          >
            {busyTest ? <Loader2 size={11} className="animate-spin inline" /> : "Test"}
          </button>
          <button
            type="button"
            onClick={disable}
            data-testid="coach-push-off-btn"
            className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] px-2 py-1"
            title="Turn off notifications on this device only"
          >
            Off
          </button>
          {DiagLink}
        </div>
        {DiagPanel}
      </div>
    );
  }

  // idle / subscribing
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="inline-flex items-center gap-2">
        <button
          type="button"
          onClick={enable}
          disabled={status === "subscribing"}
          data-testid="coach-push-enable-btn"
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-navy)] hover:text-white hover:bg-[var(--tir-navy)] border border-[var(--tir-navy)] px-3 py-1.5 transition-colors disabled:opacity-60"
        >
          {status === "subscribing" ? (
            <>
              <Loader2 size={11} className="animate-spin" /> Enabling…
            </>
          ) : (
            <>
              <Bell size={11} /> Enable notifications
            </>
          )}
        </button>
        {DiagLink}
      </div>
      {lastError && (
        <div className="text-[10px] text-[var(--tir-burgundy)] max-w-xs text-right" data-testid="coach-push-last-error">
          {lastError}
        </div>
      )}
      {DiagPanel}
    </div>
  );
}
